import { computed, ref } from 'vue';

export interface ColumnCustomizeOption {
    key: string;
    label: string;
}

export interface TableColumnSettings {
    visibleColumnKeys: string[];
    columnOrder: string[];
}

function uniqueKnownKeys(keys: readonly string[], allKeys: readonly string[]): string[] {
    return keys.reduce<string[]>((result, key) => {
        if (allKeys.includes(key) && !result.includes(key)) result.push(key);
        return result;
    }, []);
}

export function normalizeColumnOrder(
    order: readonly string[],
    allKeys: readonly string[],
): string[] {
    const next = uniqueKnownKeys(order, allKeys);

    allKeys.forEach((key, defaultIndex) => {
        if (next.includes(key)) return;

        let insertAt = next.length;
        let hasPredecessor = false;
        for (let i = defaultIndex - 1; i >= 0; i -= 1) {
            const predecessorIndex = next.indexOf(allKeys[i]);
            if (predecessorIndex !== -1) {
                insertAt = predecessorIndex + 1;
                hasPredecessor = true;
                break;
            }
        }

        if (!hasPredecessor) {
            for (let i = defaultIndex + 1; i < allKeys.length; i += 1) {
                const successorIndex = next.indexOf(allKeys[i]);
                if (successorIndex !== -1) {
                    insertAt = successorIndex;
                    break;
                }
            }
        }

        next.splice(insertAt, 0, key);
    });

    return next;
}

export function normalizeTableColumnSettings(
    settings: TableColumnSettings | null,
    allColumns: readonly ColumnCustomizeOption[],
    lockedColumnKeys: readonly string[] = [],
): TableColumnSettings {
    const allKeys = allColumns.map((column) => column.key);
    if (settings === null) {
        return { visibleColumnKeys: [...allKeys], columnOrder: [...allKeys] };
    }

    const newKeys = allKeys.filter((key) => !settings.columnOrder.includes(key));
    const visibleColumnKeys = uniqueKnownKeys(
        [...settings.visibleColumnKeys, ...newKeys, ...lockedColumnKeys],
        allKeys,
    );

    return {
        visibleColumnKeys,
        columnOrder: normalizeColumnOrder(settings.columnOrder, allKeys),
    };
}

function useTableColumns(
    allColumns: readonly ColumnCustomizeOption[],
    lockedColumnKeys: readonly string[] = [],
) {
    const defaults = normalizeTableColumnSettings(null, allColumns, lockedColumnKeys);
    const visibleColumnKeys = ref<string[]>(defaults.visibleColumnKeys);
    const columnOrder = ref<string[]>(defaults.columnOrder);

    const orderedVisibleColumnKeys = computed(() => {
        const locked = lockedColumnKeys as string[];
        const effective = [...locked];
        visibleColumnKeys.value.forEach((key) => {
            if (!effective.includes(key)) effective.push(key);
        });
        return columnOrder.value.filter((key) => effective.includes(key));
    });

    function applySettings(settings: TableColumnSettings | null) {
        const normalized = normalizeTableColumnSettings(
            settings,
            allColumns,
            lockedColumnKeys,
        );
        visibleColumnKeys.value = normalized.visibleColumnKeys;
        columnOrder.value = normalized.columnOrder;
    }

    return {
        visibleColumnKeys,
        columnOrder,
        orderedVisibleColumnKeys,
        applySettings,
    };
}

export default useTableColumns;
